import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, AnchorHTMLAttributes } from 'react'

/**
 * 의존성 없는 경량 라우터 (SSR/프리렌더 친화).
 * - 2~수 개 페이지 규모에 맞춰 History API만 사용.
 * - 서버에서는 initialPath로 경로를 주입하고, 클라이언트에서는 location.pathname을 사용.
 * - 추후 /blog, /blog/:slug 확장 시 RouteView의 분기만 늘리면 됨.
 */
interface RouterContextType {
    path: string
    navigate: (to: string) => void
}

const RouterContext = createContext<RouterContextType>({ path: '/', navigate: () => {} })

const normalize = (p: string): string => {
    const base = (p || '/').split('#')[0].split('?')[0]
    if (base.length > 1 && base.endsWith('/')) return base.slice(0, -1)
    return base || '/'
}

export const RouterProvider: React.FC<{ children: ReactNode; initialPath?: string }> = ({ children, initialPath }) => {
    const [path, setPath] = useState<string>(() => {
        if (initialPath) return normalize(initialPath)
        if (typeof window !== 'undefined') return normalize(window.location.pathname)
        return '/'
    })

    useEffect(() => {
        const onPop = () => setPath(normalize(window.location.pathname))
        window.addEventListener('popstate', onPop)
        return () => window.removeEventListener('popstate', onPop)
    }, [])

    const navigate = useCallback((to: string) => {
        if (typeof window === 'undefined') return
        const targetPath = normalize(to)
        const hash = to.includes('#') ? to.split('#')[1] : ''
        const samePage = targetPath === normalize(window.location.pathname)

        window.history.pushState({}, '', to)
        setPath(targetPath)

        if (hash) {
            // 같은 페이지 내 섹션 이동이면 부드럽게, 페이지 전환이면 렌더 후 스크롤
            const doScroll = () => document.getElementById(hash)?.scrollIntoView({ behavior: samePage ? 'smooth' : 'auto' })
            samePage ? doScroll() : setTimeout(doScroll, 80)
        } else {
            window.scrollTo({ top: 0, behavior: 'auto' })
        }
    }, [])

    return <RouterContext.Provider value={{ path, navigate }}>{children}</RouterContext.Provider>
}

export const useRouter = (): RouterContextType => useContext(RouterContext)

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    to: string
    children: ReactNode
}

export const Link: React.FC<LinkProps> = ({ to, children, onClick, ...rest }) => {
    const { navigate } = useRouter()
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.defaultPrevented) return
        // 새 탭/수정키/외부링크는 기본 동작 유지
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        if (to.startsWith('http') || rest.target === '_blank') return
        e.preventDefault()
        navigate(to)
        onClick?.(e)
    }
    return (
        <a href={to} onClick={handleClick} {...rest}>
            {children}
        </a>
    )
}
